import { useCallback, useEffect, useState } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItemCategory } from "@/Common/Models/Portal/SharedItem/SharedItemCategory";
import { SharedItemFile } from "@/Common/Models/Portal/SharedItem/SharedItemFile";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { LinkButton } from "@/DesignSystem/Molecules/LinkButton/LinkButton";
import { TextAreaAutoSize } from "../Inputs/TextAreaAutoSize";

import styles from "./PortalCommunicationMessageItem.module.scss";

interface PortalCommunicationMessageItemProps {
    sharedItemMessage: SharedItemMessage;
    categoryId?: string;
    isUnCollapsed?: boolean;
    isEditable?: boolean;
    files?: SharedItemFile[];
    sharedItemStatus?: PortalSyncStatus;
    /**
     * Only used for a message that has at least one attachment,
     * in order to display sync or revoke button for the attachment within a message.
     */
    includeLinkedItem?: boolean;
    /**
     * Only used for a message that has at least one attachment,
     * in order to display the date of the last status change when the message was revoked
     */
    sharedItemLastStatusDate?: Date;
    onSharedItemMessageStatusToggleAsync?: (status: PortalSyncStatus.Revoked | PortalSyncStatus.Pending) => Promise<void>;
    /**
     * Only used for a message that has at least one attachment,
     * in order to sync or revoke an attachment in a shared item.
     */
    onSharedItemAttachmentStatusToggleAsync?: (status: PortalSyncStatus.Revoked | PortalSyncStatus.Pending) => Promise<void>;
    onPriorityChange(selectedPriority: SharedItemPriority): void;
    onCategoryChange(selectedCategoryLookupId: string): void;
}

function PortalCommunicationMessageItem(props: Readonly<PortalCommunicationMessageItemProps>): JSX.Element {
    const { getText } = useTranslation();
    const [isCollapsed, setIsCollapsed] = useState(!props.isUnCollapsed);
    const [priority, setPriority] = useState<SharedItemPriority>(props.sharedItemMessage.Priority ?? SharedItemPriority.Normal);
    const [isStatusChangeInProgress, setIsStatusChangeInProgress] = useState(false);
    const [isSharedItemStatusChangeInProgress, setIsSharedItemStatusChangeInProgress] = useState(false);
    const [categories, setCategories] = useState<SharedItemCategory[]>([]);

    const getSharedCategoriesAsync = useCallback(async (): Promise<void> => {
        const categories = await cacheStore.getSharedItemCategories();

        setCategories(categories);
    }, []);

    useEffect(() => {
        getSharedCategoriesAsync();
    }, [getSharedCategoriesAsync]);

    if (!props.sharedItemMessage)
        return null;

    const toggleCollapsed = (): void => {
        setIsCollapsed(!isCollapsed);
    };

    const formatDate = (date: Date): string => LuxonDateTimeParser.parse(date)?.toFormat(`yyyy/MM/dd '${getText("AtLabel")}' HH:mm`);

    const handlePriorityChange = (selectedPriority: SharedItemPriority): void => {
        setPriority(selectedPriority);
        props.onPriorityChange(selectedPriority);
    };

    const renderMessageReadDate = (): string => {
        if (props.sharedItemMessage.Status === PortalSyncStatus.Viewed) {
            return formatDate(props.sharedItemMessage.LastStatusDate);
        }

        if (props.sharedItemMessage.Status === PortalSyncStatus.Revoked) {
            return getText("SharedItem_MessageRevokedLabel");
        }

        return getText("UnreadLabel");
    };

    const isSharedItemMessageRevoked = props.sharedItemMessage.Status === PortalSyncStatus.Revoked;

    const isSharedItemAttachmentRevoked = !props.includeLinkedItem
        || props.sharedItemStatus === PortalSyncStatus.Revoked;

    const toggleStatus = async (): Promise<void> => {
        if (isStatusChangeInProgress) {
            return;
        }

        try {
            setIsStatusChangeInProgress(true);

            await props.onSharedItemMessageStatusToggleAsync?.(isSharedItemMessageRevoked ? PortalSyncStatus.Pending : PortalSyncStatus.Revoked);
        }
        finally {
            setIsStatusChangeInProgress(false);
        }
    };

    const toggleSharedItemStatus = async (): Promise<void> => {
        try {
            setIsSharedItemStatusChangeInProgress(true);
            await props.onSharedItemAttachmentStatusToggleAsync?.(isSharedItemAttachmentRevoked ? PortalSyncStatus.Pending : PortalSyncStatus.Revoked);
        }
        finally {
            setIsSharedItemStatusChangeInProgress(false);
        }
    };

    const renderPriorityElement = (): JSX.Element => {
        if (props.isEditable) {
            return (
                <div className={styles.messageRow}>
                    <div className={styles.selectorLabel}>{getText("PriorityLabel")}</div>
                    <div className={styles.selectorContainer}>
                        <div className={styles.selectorElement}>
                            <select
                                onChange={e => handlePriorityChange(parseInt(e.target.value))}
                                className="form-control"
                                value={priority}
                                data-cy="portal-communications-selected-priority"
                            >
                                {EnumHelper.getNamesAndValues(SharedItemPriority).map((priority, index) => (
                                    <option
                                        key={`${index}_${priority.name}`}
                                        value={priority.value}
                                    >
                                        {getText(`Enums_SharedItemPriority_${priority.name}`)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.priorityHighLink}>
                            {priority !== SharedItemPriority.High && (
                                <a onClick={() => handlePriorityChange(SharedItemPriority.High)} data-cy="portal-communications-message-priority">
                                    {getText("Enums_SharedItemPriority_High")}
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div className={styles.messageRow}>
                <div>{getText("PriorityLabel")}</div>
                <div>
                    <span>
                        {!IsNullOrUndefined(props.sharedItemMessage.Priority) ? getText(`Enums_SharedItemPriority_${SharedItemPriority[props.sharedItemMessage.Priority]}`) : ""}
                    </span>
                </div>
            </div>
        );
    };

    const renderCategoryElement = (): JSX.Element => {
        const renderCategoryLabel = (): JSX.Element => (
            <div>
                <span>
                    {props.categoryId
                        ? cacheStore.GetLocalizedValueByLookupId(categories.find(p => p.Id === props.categoryId)?.NameLookupId)
                        : getText("SharedItem_SharedItemCategory_ClinicInitiated")
                    }
                </span>
            </div>
        );
        const renderCategoryDropdown = (): JSX.Element => (
            <div className={styles.selectorContainer}>
                <div className={styles.selectorElement}>
                    <select
                        onChange={e => props.onCategoryChange(e.target.value)}
                        className="form-control"
                        value={props.categoryId}
                        data-cy="portal-communications-selected-category"
                    >
                        {categories
                            .map(category => (
                                <option
                                    key={category.Id}
                                    value={category.Id}
                                >
                                    {cacheStore.GetLocalizedValueByLookupId(category.NameLookupId)}
                                </option>
                            ))}
                    </select>
                </div>
            </div>
        );

        // null categoryId is considered as clinic initiated
        const isInitiatedByClinic = !props.categoryId;
        return (
            <div className={styles.messageRow}>
                <div className={isInitiatedByClinic ? "" : styles.selectorLabel}>{getText("CategoryDLabel")}</div>
                {isInitiatedByClinic ? renderCategoryLabel() : renderCategoryDropdown()}
            </div>
        );
    };

    const renderAttachmentReadOnRow = (): JSX.Element => {
        if (!props.files?.length) {
            return null;
        }

        let lastStatusDate = getText("UnreadLabel");
        if (props.includeLinkedItem && props.sharedItemStatus === PortalSyncStatus.Viewed) {
            lastStatusDate = formatDate(props.sharedItemLastStatusDate);
        }
        else if (isSharedItemAttachmentRevoked) {
            lastStatusDate = getText("SharedItem_AttachmentRevokedLabel");
        }

        return (
            <div className={styles.messageRow}>
                <div>{getText("SharedItem_AttachmentReadOnLabel")}</div>
                <div>
                    <span className={`${styles.SharedItemMessage} ${isSharedItemAttachmentRevoked ? "color-red" : ""}`}>
                        {lastStatusDate}
                    </span>
                    <LinkButton
                        label={isSharedItemAttachmentRevoked ? getText("SyncLabel") : getText("RevokeLabel")}
                        variant={isSharedItemAttachmentRevoked ? "brand" : "error"}
                        onClick={() => toggleSharedItemStatus()}
                        disabled={isSharedItemStatusChangeInProgress}
                    />
                </div>
            </div>
        );
    };

    const getFileUrl = (file: SharedItemFile): string => apiSettings
        .extendUrl(
            new URL(file.FileUri),
            {
                auth: true,
                params: {
                    v: Date.now()
                }
            }
        ).toString();

    const openFileInNewWindow = (file: SharedItemFile): void => {
        const newWindow = window.open(getFileUrl(file), "_blank", "height = 480, width = 600, modal=yes, alwaysRaised=yes");
        if (newWindow.focus) {
            newWindow.focus();
        }
    };

    const senderName = props.sharedItemMessage.IsFromPatient
        ? NameHelper.formatFullName(props.sharedItemMessage.PatientFirstName, props.sharedItemMessage.PatientLastName, props.sharedItemMessage.PatientPreferredName)
        : cacheStore.GetCliniciansPersonsByContactId(props.sharedItemMessage.SentByContactId)?.NameWithPrefix;

    // check if the message is fake (generated from the shared item to show the attachments)
    const isAttachmentOnlyMessage = !props.sharedItemMessage.SharedItemMessageId;

    return (
        <div className={styles.messageContainer}>
            <div className={styles.messageHeader} onClick={toggleCollapsed} data-cy="portal-communications-message-header">
                <div>
                    <span className={`${styles.inlineTitle} bold`}>{getText("FromLabel")}</span>
                    <span data-cy="portal-communications-message-from">{senderName}</span>
                    <span className={styles.inlineTitle}>{getText("OnLabel").toLowerCase()}</span>
                    <span className={styles.dateCreated} data-cy="portal-communications-message-date">{formatDate(props.sharedItemMessage.DateCreated)}</span>
                </div>
            </div>
            <div className={`${styles.messageBody} ${isCollapsed ? styles.collapsed : ""}`} data-cy="portal-communications-message-body">
                <div className={styles.messageData}>
                    {props.sharedItemMessage.IsFromPatient && (
                        <>
                            {renderPriorityElement()}
                            {props.isEditable && renderCategoryElement()}
                        </>
                    )}
                    {!props.sharedItemMessage.IsFromPatient && (
                        <>
                            {renderPriorityElement()}
                            {props.isEditable && renderCategoryElement()}
                            {!isAttachmentOnlyMessage && (
                                <div className={styles.messageRow}>
                                    <div>{getText("SharedItem_MessageReadOnLabel")}</div>
                                    <div>
                                        <span
                                            data-cy="portal-communications-message-date-read"
                                            className={`${styles.SharedItemMessage} ${isSharedItemMessageRevoked ? "color-red" : ""}`}
                                        >
                                            {renderMessageReadDate()}
                                        </span>
                                        <LinkButton
                                            label={isSharedItemMessageRevoked ? getText("SyncLabel") : getText("RevokeLabel")}
                                            variant={isSharedItemMessageRevoked ? "brand" : "error"}
                                            onClick={() => toggleStatus()}
                                            disabled={isStatusChangeInProgress}
                                        />
                                    </div>
                                </div>
                            )}
                            {renderAttachmentReadOnRow()}
                        </>
                    )}
                    <div className={`${styles.fullWidth} ${styles.separatorLine}`} />
                    {!isAttachmentOnlyMessage && (
                        <div className={styles.messageRow}>
                            <div>{getText("MessageLabel")}</div>
                            <div>
                                <TextAreaAutoSize
                                    id="message_item_view"
                                    dataCy="portal-communications-message-text"
                                    cssClass={styles.textMessage}
                                    value={props.sharedItemMessage.Text}
                                    maxHeight="150px"
                                    disabled
                                />
                            </div>
                        </div>
                    )}
                    {!!props.files?.length && (
                        <div className={styles.messageRow}>
                            <div>{getText("AttachmentsLabel")}</div>
                            {props.files?.map((file, index) => (<div key={`${file.FileName}${index}`}><a onClick={() => openFileInNewWindow(file)} target="_blank">{file.FileName}</a></div>))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export { PortalCommunicationMessageItem };
